import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import styles from "./index.module.css";

import { trackEvent } from "@/lib/plausible";
import Link from "next/link";

type Punchline = {
  pre: string;
  post: string;
  sub: string;
};

type ChatMessage = {
  role: "user" | "ai";
  text: string;
  chips?: string[];
  cta?: boolean;
  githubCta?: boolean;
};

type ChatState = {
  step: number;
  messages: ChatMessage[];
  input: string;
  setInput: (v: string) => void;
  typing: boolean;
  send: (text: string) => void;
  reset: () => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
};

const VIBE_APPS = [
  "a habit tracker for your situationship",
  "a dating app for cats",
  "an AI that rates your fridge",
  "a Notion alternative for therapists",
  "a recipe app that yells at you",
  "a Twitter clone but for plants",
  "a ChatGPT wrapper your mom will love",
  "yet another startup idea generator"
];

const AI_REPLIES = [
  {
    icon: true,
    text: "got it — sounds like a Next.js app. nice 🫡\n\nwhere's the code right now? you can paste a GitHub link, or tell me if it's still on your laptop.",
    chips: ["github.com/me/my-app", "it's only on my laptop", "I'll connect GitHub"]
  },
  {
    text: "perfect. last thing — what URL would you like?\n\nyou can use a custom domain, or grab a free one like `yourapp.dollardeploy.app`.",
    chips: ["myapp.dollardeploy.app", "I have my own domain", "pick one for me"]
  },
  {
    text: "love it. here's the plan:\n\n• small server ($4/mo) in EU\n• we build your Next.js app from `main`\n• HTTPS auto-configured\n• every git push redeploys\n\nready to make it live?",
    cta: true
  }
] as const;

const GITHUB_CTA_REPLY: ChatMessage = {
  role: "ai",
  text: "perfect — GitHub is the fastest path. we'll hook up your repo and auto-deploy on every push.",
  githubCta: true
};

const isGitHubMessage = (text: string) =>
  /github|git\.com|connect\s*git|i'?ll\s*connect/i.test(text);

const useStableTypewriter = (words: string[]) => {
  const [text, setText] = useState(words[0] ?? "");
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"typing" | "hold" | "erasing">("hold");

  useEffect(() => {
    if (!words.length) {
      return;
    }
    const word = words[idx % words.length];
    let t: ReturnType<typeof setTimeout>;
    if (phase === "typing") {
      if (text.length < word.length) {
        t = setTimeout(() => setText(word.slice(0, text.length + 1)), 60);
      } else {
        t = setTimeout(() => setPhase("hold"), 1600);
      }
    } else if (phase === "hold") {
      t = setTimeout(() => setPhase("erasing"), 1600);
    } else {
      if (text.length > 0) {
        t = setTimeout(() => setText(text.slice(0, -1)), 28);
      } else {
        setIdx(x => x + 1);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, words]);

  return text;
};

const useChatFlow = (): ChatState => {
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const send = (text: string) => {
    if (!text.trim()) {
      return;
    }
    setMessages(m => [...m, { role: "user", text }]);
    setInput("");
    setTyping(true);

    if (messages.length === 0) {
      trackEvent("L2_ChatStarted");
    }

    // GitHub messages shortcut to the deploy card at any step
    if (isGitHubMessage(text)) {
      trackEvent("L2_GitHubSelected");
      setTimeout(() => {
        setMessages(m => [...m, GITHUB_CTA_REPLY]);
        setTyping(false);
        setStep(99);
      }, 950);
      return;
    }

    const reply = AI_REPLIES[step];
    if (!reply) {
      setTyping(false);
      return;
    }
    setTimeout(() => {
      setMessages(m => [
        ...m,
        {
          role: "ai" as const,
          text: reply.text,
          chips: "chips" in reply ? [...reply.chips] : undefined,
          cta: "cta" in reply ? reply.cta : undefined
        }
      ]);
      setTyping(false);
      setStep(s => s + 1);
    }, 950);
  };

  const reset = () => {
    setStep(0);
    setMessages([]);
    setInput("");
    setTyping(false);
  };

  return { step, messages, input, setInput, typing, send, reset, scrollRef };
};

const GitHubIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.6v-2.2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.8-1.6-2.5-.3-5.2-1.3-5.2-5.7 0-1.3.4-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.2 1.2.9-.3 1.9-.4 2.9-.4s2 .1 2.9.4c2.2-1.5 3.2-1.2 3.2-1.2.6 1.6.2 2.8.1 3.1.7.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.2 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z" />
  </svg>
);

const GitHubDeployCard = () => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(v => {
      if (!v) {
        trackEvent("L2_GitHubCardOpened");
      }
      return !v;
    });
  };

  return (
    <div className={styles.ghCard}>
      <button
        type="button"
        className={styles.ghCardHeader}
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls="gh-deploy-panel"
      >
        <span className={styles.ghCardIcon}>
          <GitHubIcon size={18} />
        </span>
        <span className={styles.ghCardTitle}>Connect GitHub &amp; deploy</span>
        <span
          className={cn(styles.ghCardChevron, open && styles.ghCardChevronOpen)}
          aria-hidden="true"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div id="gh-deploy-panel" className={styles.ghCardBody}>
          <p className={styles.ghCardDesc}>
            We&apos;ll link your repo and auto-redeploy on every{" "}
            <code className={styles.ghCardCode}>git push</code>. Takes about 2 minutes.
          </p>
          <Link
            href="/dashboard"
            className={`${styles.ghCardCta} plausible-event-name=L2_CreateAccount`}
          >
            Create an account and deploy →
          </Link>
        </div>
      )}
    </div>
  );
};

const TypingDots = () => (
  <div className={styles.msgRow}>
    <div className={styles.aiAvatar} aria-hidden="true">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </div>
    <div
      className={cn(styles.bubble, styles.bubbleAi, styles.bubbleTyping)}
      aria-label="AI is typing"
    >
      <span className={styles.dot} style={{ animationDelay: "0ms" }} />
      <span className={styles.dot} style={{ animationDelay: "150ms" }} />
      <span className={styles.dot} style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

const ChatMessageItem = ({ m, onChip }: { m: ChatMessage; onChip: (t: string) => void }) => {
  if (m.role === "user") {
    return (
      <div className={cn(styles.msgRow, styles.msgRowUser)}>
        <div className={cn(styles.bubble, styles.bubbleUser)}>{m.text}</div>
      </div>
    );
  }
  return (
    <div className={styles.msgRow}>
      <div className={styles.aiAvatar} aria-hidden="true">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </div>
      <div className={styles.msgBody}>
        <div className={cn(styles.bubble, styles.bubbleAi)}>
          {m.text.split("\n").map((line, i) => (
            <p key={i} className={styles.bubbleLine}>
              {line || " "}
            </p>
          ))}
        </div>
        {m.githubCta && <GitHubDeployCard />}
        {m.chips && (
          <div className={styles.msgChips}>
            {m.chips.map(c => (
              <button key={c} className={styles.msgChip} onClick={() => onChip(c)}>
                {c}
              </button>
            ))}
          </div>
        )}
        {m.cta && (
          <div className={styles.msgCtaRow}>
            <Link href="/dashboard" className={styles.ctaPrimary}>
              Create account &amp; deploy →
            </Link>
            <Link href="/pricing" className={styles.ctaSecondary}>
              See the plan first
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const INITIAL_CHIPS = [
  { label: "Deploy my Next.js app", icon: "▲" },
  { label: "I have no idea what I'm doing", icon: "🫠" },
  { label: "Move my app off localhost", icon: "→" },
  { label: "Just give me a real URL", icon: "✦" }
];

const ChatWidget = ({ chat }: { chat: ChatState }) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    chat.send(chat.input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      chat.send(chat.input);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    chat.setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  const placeholder =
    chat.messages.length === 0
      ? 'Tell us what you built. e.g. "I made a Next.js app for tracking my plants…"'
      : "Reply…";

  return (
    <div className={styles.chat}>
      {chat.messages.length > 0 && (
        <div
          // eslint-disable-next-line react-hooks/refs
          ref={chat.scrollRef}
          className={styles.chatThread}
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
        >
          {/* eslint-disable-next-line react-hooks/refs */}
          {chat.messages.map((m, i) => (
            <ChatMessageItem key={i} m={m} onChip={t => chat.send(t)} />
          ))}
          {/* eslint-disable-next-line react-hooks/refs */}
          {chat.typing && <TypingDots />}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.chatInputCard}>
        <textarea
          rows={1}
          // eslint-disable-next-line react-hooks/refs
          value={chat.input}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.chatTextarea}
          aria-label="Describe your app"
        />
        <div className={styles.chatActions}>
          <div className={styles.chatActionsLeft}>
            <button
              type="button"
              className={styles.actionBtn}
              title="Connect GitHub"
              aria-label="Connect GitHub"
              onClick={() => chat.send("I'll connect GitHub")}
            >
              <GitHubIcon size={16} />
              <span>GitHub</span>
            </button>
            {/* eslint-disable-next-line react-hooks/refs */}
            {chat.messages.length > 0 && (
              <button
                type="button"
                className={cn(styles.actionBtn, styles.iconOnly)}
                // eslint-disable-next-line react-hooks/refs
                onClick={chat.reset}
                title="Reset chat"
                aria-label="Reset chat"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <path d="M3 4v5h5" />
                </svg>
              </button>
            )}
          </div>
          <div className={styles.chatActionsRight}>
            <span className={styles.modelChip}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="6" />
              </svg>
              dd-deploy
            </span>
            <button
              type="submit"
              className={styles.sendBtn}
              // eslint-disable-next-line react-hooks/refs
              disabled={!chat.input.trim()}
              aria-label="Send message"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
              </svg>
            </button>
          </div>
        </div>
      </form>

      {/* eslint-disable-next-line react-hooks/refs */}
      {chat.messages.length === 0 && (
        <div className={styles.chipsRow} role="group" aria-label="Quick start options">
          {INITIAL_CHIPS.map(c => (
            <button key={c.label} className={styles.chip} onClick={() => chat.send(c.label)}>
              <span className={styles.chipIcon} aria-hidden="true">
                {c.icon}
              </span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Hero = ({ punchline }: { punchline: Punchline }) => {
  const typed = useStableTypewriter(VIBE_APPS);
  const chat = useChatFlow();

  return (
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <div className={styles.heroKicker} aria-label="For vibe coders">
          <span className={styles.kickerDot} aria-hidden="true" />
          for vibe coders, cursor kids &amp; &ldquo;claude wrote it&rdquo; enjoyers
        </div>

        <h1 className={styles.heroH1}>
          <div>{punchline.pre}</div>{" "}
          <span className={styles.typewriter} aria-live="polite" aria-label={`"${typed}"`}>
            <span className={styles.typewriterPrefix} aria-hidden="true">
              &ldquo;
            </span>
            <span className={styles.typewriterText}>{typed}</span>
            <span className={styles.typewriterCaret} aria-hidden="true">
              |
            </span>
            <span className={styles.typewriterSuffix} aria-hidden="true">
              &rdquo;
            </span>
          </span>
          <br />
          <span className={styles.h1Emph}>{punchline.post}</span>
        </h1>

        <p className={styles.heroSub}>{punchline.sub}</p>

        <ChatWidget chat={chat} />

        <div className={styles.trustRow} aria-label="Trust indicators">
          <span className={styles.trustItem}>
            <span className={styles.trustDot} aria-hidden="true" />
            no credit card
          </span>
          <span className={styles.trustDivider} aria-hidden="true" />
          <span className={styles.trustItem}>14-day free trial</span>
          <span className={styles.trustDivider} aria-hidden="true" />
          <span className={styles.trustItem}>servers from $4/mo</span>
          <span className={styles.trustDivider} aria-hidden="true" />
          <span className={styles.trustItem}>cancel anytime</span>
        </div>
      </div>
    </section>
  );
};
